// src/components/widgets/AIConfigWidget.tsx
// Phase 1: AI Configuration Widget - Migrated from ConfigView with FULL functionality
import React, { useState } from 'react';
import { AIConfig, AIModel } from '../../types';
import AIConfigModal from '../modals/AIConfigModal';

interface AIConfigWidgetProps {
    aiConfig: AIConfig | null;
    testwerkApiKey: string;
    setTestwerkApiKey: (key: string) => void;
    agentApiKey: string;
    setAgentApiKey: (key: string) => void;
    selectedModel: AIModel;
    setSelectedModel: (model: AIModel) => void;
    onUpdateAIConfig: () => void;
}

// Helper function for provider detection (from ConfigView)
const getProviderFromModel = (model?: string): string => {
    if (!model) return 'Unknown';
    
    // Handle local models first (format: "modelname:version")
    if (model.includes(':')) {
        const baseModel = model.split(':')[0];
        return `${baseModel.charAt(0).toUpperCase() + baseModel.slice(1)} (Local)`;
    }
    
    // Cloud models
    if (model.includes('gpt')) return 'OpenAI';
    if (model.includes('claude')) return 'Anthropic';
    if (model.includes('gemini')) return 'Google';
    if (model.includes('mistral')) return 'Mistral Cloud';
    
    return 'Unknown';
};

const AIConfigWidget: React.FC<AIConfigWidgetProps> = ({
    aiConfig,
    testwerkApiKey,
    setTestwerkApiKey,
    agentApiKey,
    setAgentApiKey,
    selectedModel,
    setSelectedModel,
    onUpdateAIConfig
}) => {
    // Local state for AI Config Modal
    const [showAIConfigModal, setShowAIConfigModal] = useState(false);

    return (
        <>
            <div className="dashboard-widget">

                <h3>AI Configuration</h3>
                {/*
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <span
                            style={{ cursor: 'pointer', fontSize: '1.2rem' }}
                            onClick={() => setShowAIConfigModal(true)}
                            title="Configure AI Settings"
                        >
                            ⚙️
                        </span>
                        <span
                            style={{
                                cursor: aiConfig?.testwerk_api_key_configured ? 'pointer' : 'not-allowed',
                                fontSize: '1.2rem',
                                opacity: aiConfig?.testwerk_api_key_configured ? 1 : 0.4
                            }}
                            onClick={aiConfig?.testwerk_api_key_configured ? onUpdateAIConfig : undefined}
                            title="Refresh AI Config"
                        >
                            🔄
                        </span>
                    </div>*/}


                <p>AI system status and configuration management</p>

                {/* Provider-aware status display */}
                <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1.5rem' }}>
                    <div style={{
                        padding: '0.75rem',
                        background: aiConfig?.testwerk_api_key_configured ? '#d4edda' : '#f8d7da',
                        borderRadius: '6px',
                        border: `1px solid ${aiConfig?.testwerk_api_key_configured ? '#c3e6cb' : '#f1c2c7'}`
                    }}>
                        <strong>TestWerk.ai API ({getProviderFromModel(aiConfig?.default_model)}):</strong>
                        {aiConfig?.testwerk_api_key_configured ? (
                            <span style={{ color: '#155724' }}> ✅ Configured</span>
                        ) : (
                            <span style={{ color: '#721c24' }}> ❌ Not Configured</span>
                        )}
                    </div>

                    <div style={{
                        padding: '0.75rem',
                        background: aiConfig?.agent_api_key_configured ? '#d4edda' : '#fff3cd',
                        borderRadius: '6px',
                        border: `1px solid ${aiConfig?.agent_api_key_configured ? '#c3e6cb' : '#ffeaa7'}`
                    }}>
                        <strong>Agent API:</strong>
                        {aiConfig?.agent_api_key_configured ? (
                            <span style={{ color: '#155724' }}> ✅ Configured</span>
                        ) : (
                            <span style={{ color: '#856404' }}> ⚪ Optional</span>
                        )}
                    </div>

                    <div style={{
                        padding: '0.75rem',
                        background: aiConfig?.default_model ? '#d4edda' : '#f8d7da',
                        borderRadius: '6px',
                        border: `1px solid ${aiConfig?.default_model ? '#c3e6cb' : '#f1c2c7'}`
                    }}>
                        <strong>AI Model:</strong>
                        {aiConfig?.default_model ? (
                            <span style={{ color: '#155724', marginLeft: '0.5rem' }}>
                                ✅ {aiConfig.default_model}
                            </span>
                        ) : (
                            <span style={{ color: '#721c24', marginLeft: '0.5rem' }}>
                                ❌ Not set
                            </span>
                        )}
                    </div>
                </div>

                {/* Performance Metrics - Compact Version */}
                <div style={{
                    marginTop: '1.5rem',
                    marginBottom: '0.5rem',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #dee2e6'
                }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem' }}>📊 Performance Metrics</h4>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '0.75rem'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#dc2626' }}>
                                {aiConfig ? '2.4s' : 'N/A'}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#666' }}>Avg Response</div>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#16a34a' }}>
                                {aiConfig ? '87%' : 'N/A'}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#666' }}>Cache Hit Rate</div>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#0891b2' }}>
                                {aiConfig ? '15.2K' : 'N/A'}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#666' }}>Tokens Today</div>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#7c3aed' }}>
                                {aiConfig ? '$12.45' : 'N/A'}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#666' }}>Est. Cost</div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions 
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '0.5rem',
                    marginTop: '1rem'
                }}>
                    
                    <button
                        className="pushable push-warning btn-sm"
                        onClick={() => setShowAIConfigModal(true)}
                    >
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">🔧 Configure</span>
                    </button>
                    
                    <button
                        className="pushable push-secondary btn-sm"
                        onClick={onUpdateAIConfig}
                        disabled={!aiConfig?.testwerk_api_key_configured}
                    >
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">🔄 Refresh</span>
                    </button>
                    
                </div>
                    */}


                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
                    <span
                        style={{ color: 'dodgerblue', fontSize: '2.5rem', lineHeight: 1, cursor: 'pointer' }}
                        onClick={() => setShowAIConfigModal(true)}
                        title="Configure AI Settings"
                    >
                        ⚙︎
                    </span>
                    <span
                        style={{
                            cursor: aiConfig?.testwerk_api_key_configured ? 'pointer' : 'not-allowed',
                            fontSize: '1.4rem',
                            lineHeight: 1,
                            opacity: aiConfig?.testwerk_api_key_configured ? 1 : 0.4
                        }}
                        onClick={aiConfig?.testwerk_api_key_configured ? onUpdateAIConfig : undefined}
                        title="Refresh AI Config"
                    >
                        🔄
                    </span>
                </div>



                {/* AI Configuration Warning */}
                {!aiConfig?.testwerk_api_key_configured && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#fff3cd',
                        color: '#856404',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        textAlign: 'center'
                    }}>
                        ⚠️ AI not configured - Click Settings to configure API keys
                    </div>
                )}
            </div>

            {/* FULL AI Configuration Modal - Embedded */}
            <AIConfigModal
                isOpen={showAIConfigModal}
                onClose={() => setShowAIConfigModal(false)}
                aiConfig={aiConfig}
                testwerkApiKey={testwerkApiKey}
                setTestwerkApiKey={setTestwerkApiKey}
                agentApiKey={agentApiKey}
                setAgentApiKey={setAgentApiKey}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                onUpdateConfig={onUpdateAIConfig}
            />
        </>
    );
};

export default AIConfigWidget;